import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BackGround, BottomButton, BottomContainer, Component, QuestColorBox, QuestColorContainer, QuestColorText, QuestTitle, SPFQuestColorText, TellUsTitle } from '../styles/questStyle';
import { CliniqueState, Sensitivity, SkinType, StepView, UVprotection } from '../type';
import * as ActionTypes from '../constantes/actionTypes';
import i18n from '../i18nextInit';
import { sendAnalytics, sendAnalyticsPageView } from '../constantes/utilitaries';
import BG from '../assets/Images/mobile/questBG.png';
import { Footer } from './footer';
import { useTranslation } from 'react-i18next';
import { trackPlausibleEvent } from '../services/analytics';
import { scrollQuestionnaireTo } from '../constantes/questionnaireScroll';

const Quest = () => {
	const dispatch = useDispatch();
	const isEmbedded = window.self !== window.top;
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const bottomRef = useRef<HTMLDivElement>(null);
	const sensitivityRef = useRef<HTMLDivElement>(null);
	const [scrollRequest, setScrollRequest] = useState<{ target: 'sensitivity' | 'bottom' } | null>(null);
	const sessionID = useSelector((state: CliniqueState) => state.session.sessionID);
	const market = useSelector((state: CliniqueState) => state.session.market);
	const [skintype, setSkinType] = useState<SkinType | null>(null);
	const [spfProtection, setSpfProtection] = useState<UVprotection | null>(null);
	const [sensitivity, setSensitivity] = useState<Sensitivity | null>(null);
	const { t } = useTranslation();
	const selectOneLabel = '<span style="opacity: 0.5">' + t('select-one') + '</span>';

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const skinTypeForAnlaytics = (skinType: SkinType): string => {
		switch (skinType) {
			case SkinType.dry:
				return 'dry';
			case SkinType.dryCombination:
				return 'dry_Combo';
			case SkinType.oilyCombination:
				return 'oily_combo';
			case SkinType.veryOily:
				return 'very_oily';
			default:
				return skinType;
		}
	};

	useEffect(() => {
		sendAnalyticsPageView(3, market, sessionID);
	}, [market, sessionID]);

	useEffect(() => {
		const onResize = () => setIsMobile(window.innerWidth < 768);
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	useEffect(() => {
		if (!scrollRequest) return;
		const frame = window.requestAnimationFrame(() => {
			const target = scrollRequest.target === 'sensitivity' ? sensitivityRef.current : bottomRef.current;
			if (target) scrollQuestionnaireTo(target);
		});
		return () => {
			window.cancelAnimationFrame(frame);
		};
	}, [scrollRequest]);

	const handleSkinType = (_skinType: SkinType) => {
		if (skintype === _skinType) {
			setSkinType(null);
		} else {
			setSkinType(_skinType);
			if (!skintype) setScrollRequest({ target: 'sensitivity' });
		}
	};

	const handleUVType = (_UVtype: UVprotection) => {
		if (spfProtection === _UVtype) {
			setSpfProtection(null);
		} else {
			setSpfProtection(_UVtype);
		}
	};

	const handleSensitivity = (_sensitivity: Sensitivity) => {
		if (sensitivity === _sensitivity) {
			setSensitivity(null);
		} else {
			setSensitivity(_sensitivity);
			if (!sensitivity) setScrollRequest({ target: 'bottom' });
		}
	};

	const checkIfButtonenabled = (): boolean => {
		if (spfProtection && skintype && sensitivity) {
			return true;
		}

		return false;
	};

	const handleSubmit = () => {
		if (!checkIfButtonenabled()) return;
		sendAnalytics("question 1 - describe skin type", skinTypeForAnlaytics(skintype!), sessionID)
		sendAnalytics("question 2 - describe sensitivity", sensitivity as string, sessionID)
		sendAnalytics("question 3 - describe spf preference", spfProtection as string, sessionID)
		trackPlausibleEvent('sd_questionnaire_step_completed', {
			props: { step: 1, question: 'skin_type' },
		});
		trackPlausibleEvent('sd_questionnaire_step_completed', {
			props: { step: 2, question: 'sensitivity' },
		});
		trackPlausibleEvent('sd_questionnaire_step_completed', {
			props: { step: 3, question: 'spf_preference' },
		});
		trackPlausibleEvent('sd_questionnaire_completed', {
			props: { answers: `${skintype}|${sensitivity}|${spfProtection}` },
		});

		dispatch({ type: ActionTypes.SET_SKIN_TYPE, value: skintype });
		dispatch({ type: ActionTypes.SET_UV_PROTECTION, value: spfProtection });
		dispatch({ type: ActionTypes.SET_SENSITIVITY, value: sensitivity });
		dispatch({ type: ActionTypes.SWITCH_STEP, value: StepView.pictureAnalyse });
	};

	return (
		<>
			<Component $embedded={isEmbedded} initial={{ x: window.RTL_LANGS.includes(i18n.language) || i18n.language === 'ArAr' ? '-100%' : '100%' }} animate={{ x: 0 }} exit={{ x: window.RTL_LANGS.includes(i18n.language) || i18n.language === 'ArAr' ? '100%' : '-100%' }} key='QUEST_KEY' transition={{ type: 'spring', duration: 0.5 }}>
				<BackGround src={BG} />

				<TellUsTitle>{t('tell-us-about-skin')}</TellUsTitle>

				<QuestTitle dangerouslySetInnerHTML={{ __html: t('skin-type-question').replace(t('select-one'), selectOneLabel) }} />

				<QuestColorContainer>
					<QuestColorBox onClick={() => handleSkinType(SkinType.dry)} $color={'#6BCDB3'} $isEnabled={skintype === SkinType.dry || skintype === null} $isSelected={skintype === SkinType.dry}>
						<SPFQuestColorText $title>{t('type-1')}</SPFQuestColorText>
						<QuestColorText>{t('dry-desc')}</QuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleSkinType(SkinType.dryCombination)} $color={'#DAA8E2B3'} $isEnabled={skintype === SkinType.dryCombination || skintype === null} $isSelected={skintype === SkinType.dryCombination}>
						<SPFQuestColorText $title>{t('type-2')}</SPFQuestColorText>
						<QuestColorText>{t('dry-combo-desc')}</QuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleSkinType(SkinType.oilyCombination)} $color={'#F89ABAB3'} $isEnabled={skintype === SkinType.oilyCombination || skintype === null} $isSelected={skintype === SkinType.oilyCombination}>
						<SPFQuestColorText $title>{t('type-3')}</SPFQuestColorText>
						<QuestColorText>{t('oily-combo-desc')}</QuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleSkinType(SkinType.veryOily)} $color={'#84DADEB3'} $isEnabled={skintype === SkinType.veryOily || skintype === null} $isSelected={skintype === SkinType.veryOily}>
						<SPFQuestColorText $title>{t('type-4')}</SPFQuestColorText>
						<QuestColorText>{t('oily-desc')}</QuestColorText>
					</QuestColorBox>
				</QuestColorContainer>

				<QuestTitle
					style={{ opacity: skintype ? 1 : 0.3, pointerEvents: skintype ? "all" : "none"}}
					dangerouslySetInnerHTML={{ __html: t('sensitivity-question').replace(t('select-one'), selectOneLabel) }} />

				<QuestColorContainer ref={sensitivityRef} style={{ opacity: skintype ? 1 : 0.3, pointerEvents: skintype ? "all" : "none"}}>
					<QuestColorBox onClick={() => handleSensitivity(Sensitivity.never)} $color={'white'} $isEnabled={sensitivity === Sensitivity.never || sensitivity === null} $isSelected={sensitivity === Sensitivity.never}>
						<SPFQuestColorText $title>{t('sensitivity-never')}</SPFQuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleSensitivity(Sensitivity.rarely)} $color={'white'} $isEnabled={sensitivity === Sensitivity.rarely || sensitivity === null} $isSelected={sensitivity === Sensitivity.rarely}>
						<SPFQuestColorText $title>{t('sensitivity-rarely')}</SPFQuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleSensitivity(Sensitivity.sometimes)} $color={'white'} $isEnabled={sensitivity === Sensitivity.sometimes || sensitivity === null} $isSelected={sensitivity === Sensitivity.sometimes}>
						<SPFQuestColorText $title>{t('sensitivity-sometimes')}</SPFQuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleSensitivity(Sensitivity.often)} $color={'white'} $isEnabled={sensitivity === Sensitivity.often || sensitivity === null} $isSelected={sensitivity === Sensitivity.often}>
						<SPFQuestColorText $title>{t('sensitivity-often')}</SPFQuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleSensitivity(Sensitivity.constantly)} $color={'white'} $isEnabled={sensitivity === Sensitivity.constantly || sensitivity === null} $isSelected={sensitivity === Sensitivity.constantly}>
						<SPFQuestColorText $title>{t('sensitivity-constantly')}</SPFQuestColorText>
					</QuestColorBox>
				</QuestColorContainer>

				<QuestTitle
					style={{ opacity: sensitivity ? 1 : 0.3, pointerEvents: sensitivity ? "all" : "none"}}
					dangerouslySetInnerHTML={{ __html: t('sun-protection-question').replace(t('select-one'), selectOneLabel) }} />

				<QuestColorContainer style={{ opacity: sensitivity ? 1 : 0.3, pointerEvents: sensitivity ? "all" : "none"}}>
					<QuestColorBox onClick={() => handleUVType(UVprotection.builtIn)} $color={'white'} $isEnabled={spfProtection === UVprotection.builtIn || spfProtection === null} $isSelected={spfProtection === UVprotection.builtIn}>
						<SPFQuestColorText $title>{t('dailySpfProtectionQuiz1')}</SPFQuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleUVType(UVprotection.protectionBase)} $color={'white'} $isEnabled={spfProtection === UVprotection.protectionBase || spfProtection === null} $isSelected={spfProtection === UVprotection.protectionBase}>
						<SPFQuestColorText $title>{t('dailySpfProtectionQuiz2')}</SPFQuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleUVType(UVprotection.mineral)} $color={'white'} $isEnabled={spfProtection === UVprotection.mineral || spfProtection === null} $isSelected={spfProtection === UVprotection.mineral}>
						<SPFQuestColorText $title>{t('dailySpfProtectionQuiz3')}</SPFQuestColorText>
					</QuestColorBox>

					<QuestColorBox onClick={() => handleUVType(UVprotection.highSPF)} $color={'white'} $isEnabled={spfProtection === UVprotection.highSPF || spfProtection === null} $isSelected={spfProtection === UVprotection.highSPF}>
						<SPFQuestColorText $title>{t('dailySpfProtectionQuiz4')}</SPFQuestColorText>
					</QuestColorBox>
				</QuestColorContainer>

				{isMobile && <Footer step={StepView.quests} inResult={true} />}

				<BottomContainer>
					<BottomButton type='button' disabled={!checkIfButtonenabled()} onClick={() => handleSubmit()} $enabled={checkIfButtonenabled()}>
						{t('show-results')}
					</BottomButton>
				</BottomContainer>

				<div ref={bottomRef} aria-hidden='true' />
			</Component>

			{!isMobile && <Footer step={5} inResult={true} />}
		</>
	);
};

const QuestComponent = () => {
	return <Quest />;
};

export default QuestComponent;
